import subscriptionFields from '../../../fields/subscriptionFields';
import SUBSCRIPTION_STATUS from '../../../enums/SubscriptionStatus';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import { DateTime, Interval } from 'luxon';
import Permissions from '@/app/permissions/Permissions';
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import { nsSubscription } from '@/app/i18n/namespaces';

/**
 * @param subscription
 * @param permissions
 * @param root0
 * @param root0.handleShowModalActivateSubscription
 * @param root0.handleShowModalTerminateSubscription
 * @param root0.handleShowModalScheduledTerminateSubscription
 * @param root0.handleShowModalMigrateSubscription
 * @param root0.handleShowModalUpdateSubscriptionWithOrWithoutPlan
 * @returns {Array}
 */
function GenerateActionsSubscriptionLifecycle(subscription, permissions,
    {
        handleShowModalActivateSubscription,
        handleShowModalTerminateSubscription,
        handleShowModalScheduledTerminateSubscription,
        handleShowModalMigrateSubscription,
        handleShowModalUpdateSubscriptionWithOrWithoutPlan
    }) {

    // Functions
    const _subscriptionStatusIsOneOf = (statuses) => {
        // Abort the case where the subscription is not loaded yet or where the statuses are invalid
        if (!subscription || !statuses) {
            return false;
        }

        const subscriptionStatus = subscription[subscriptionFields.STATUS.key];

        return statuses.some(status => subscriptionStatus === status.name);
    };
    const _subscriptionActivateIsInSameMonthAndStartBillingDateNotInFuture = () => {
        const hasMigrated = getObjectAttributeFromStringKey(subscription, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.HAS_MIGRATED));
        if (!subscription || hasMigrated) {
            return false;
        }
        const subscriptionActivationDate = DateTime.fromISO(subscription[subscriptionFields.ACTIVATION_DATE.key]);
        const isStartActivationDateInSameMonth = DateTime.now().hasSame(subscriptionActivationDate, 'month');

        const isSubscriptionDateInFuture = Interval.fromISO(subscription[subscriptionFields.START_BILLING_DATE.key]).isAfter(DateTime.now().endOf('day'));

        const isMigrationPossible = isStartActivationDateInSameMonth && !isSubscriptionDateInFuture;

        return isMigrationPossible;
    };

    return (
        [
            {
                icon: ICONS.SUBSCRIPTION_ACTIVATE,
                entity: 'actions.row.activate',
                namespace: nsSubscription,
                className: 'text-success',
                onClick: handleShowModalActivateSubscription,
                visible: _subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.TO_BE_ACTIVATED, SUBSCRIPTION_STATUS.TERMINATED]),
                disabled: !hasRequiredPermissions(
                    {
                        permissions,
                        requiredPermissions:
                            [
                                Permissions.SUBSCRIPTION_ACTIVATE.name,
                                Permissions.SUBSCRIPTION_PLAN_ADD_CLIENT.name,
                                Permissions.SUBSCRIPTION_PLAN_LIST_CLIENT_PLANS.name
                            ]
                    }
                ),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.SUBSCRIPTION_SCHEDULED_TERMINATE,
                entity: 'actions.row.scheduled_terminate',
                namespace: nsSubscription,
                className: 'text-danger',
                onClick: handleShowModalScheduledTerminateSubscription,
                visible: _subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.ACTIVATED, SUBSCRIPTION_STATUS.IN_STOCK]),
                disabled: !hasRequiredPermissions(
                    {
                        permissions,
                        requiredPermissions: [Permissions.SUBSCRIPTION_TERMINATE.name]
                    }
                ),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.SUBSCRIPTION_TERMINATE,
                entity: 'actions.row.terminate',
                namespace: nsSubscription,
                className: 'text-danger',
                onClick: handleShowModalTerminateSubscription,
                visible: _subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.ACTIVATED, SUBSCRIPTION_STATUS.IN_STOCK]),
                disabled: !hasRequiredPermissions(
                    {
                        permissions,
                        requiredPermissions: [Permissions.SUBSCRIPTION_TERMINATE.name]
                    }
                ),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.EDIT,
                entity: 'actions.row.update_plan',
                namespace: nsSubscription,
                onClick: handleShowModalUpdateSubscriptionWithOrWithoutPlan,
                visible: _subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.ACTIVATED]),
                disabled: !hasRequiredPermissions(
                    {
                        permissions,
                        requiredPermissions: [Permissions.MODIFY_IP_OFFER.name, Permissions.SUBSCRIPTION_PLAN_LIST_CLIENT_PLANS.name]
                    }
                ),
                tooltipForbidden: 'title.permissions.access_denied'
            },
            {
                icon: ICONS.MIGRATE,
                entity: 'actions.row.migrate',
                namespace: nsSubscription,
                className: 'text-warning',
                variant: 'critical',
                onClick: handleShowModalMigrateSubscription,
                visible: _subscriptionActivateIsInSameMonthAndStartBillingDateNotInFuture() &&
                    _subscriptionStatusIsOneOf([SUBSCRIPTION_STATUS.ACTIVATED]),
                disabled: !hasRequiredPermissions(
                    {
                        permissions,
                        requiredPermissions: [Permissions.SUBSCRIPTION_MIGRATE.name]
                    }
                ),
                tooltipForbidden: 'title.permissions.access_denied'
            }
        ]
    );
}

export default GenerateActionsSubscriptionLifecycle;